import { isObject, keyBy, omit, transform } from "lodash";
import {
	type AllergenType,
	type Dish,
	type DishSubscription,
	type DishType,
	type DishTypeSubscription,
	type Maybe,
	type Transaction,
	TransactionState,
} from "../types";
import type { LocalDishType } from "../types/mainTypes";

/**
 * Returns Set of dish type ids that are available in the fridge or in the order
 * @param fridgeDishTypes
 * @param orderDishes
 * @param orderState
 */
export const getAvailableDishTypeIds = (
	fridgeDishTypes: LocalDishType[] | null,
	orderDishes: Maybe<Dish[]> | undefined,
	orderState?: Maybe<TransactionState>,
) => {
	// Safe defaults
	const fridgeDishTypesSafe = fridgeDishTypes ?? [];
	const orderDishesSafe = orderDishes ?? [];

	// In case of active order, we want to keep these dishtypes displayed on the screen
	if (orderState && orderState !== TransactionState.Finished && orderDishesSafe.length > 0) {
		// Get dish type IDs from the active order
		const orderDishTypeIds = new Set(
			orderDishesSafe.filter((orderDish) => Boolean(orderDish?.type?.id)).map((orderDish) => orderDish.type?.id), // Non-null assertion since filtered above
		);

		// Get dish type IDs from fridge
		return new Set(
			fridgeDishTypesSafe
				.filter((dishType) => (dishType.dishes?.length ?? 0) > 0 || orderDishTypeIds.has(dishType.id))
				.map((dishType) => dishType.id),
		);
	}

	// Once, the order is finished, we will display only dishtypes that are available in fridge
	return new Set(fridgeDishTypesSafe.filter((dishType) => dishType?.dishes?.length).map((dishType) => dishType.id));
};

/**
 * A spotlight TOGGLE echo from the BE is a SPARSE frame: its `dishes.types` carries only the changed
 * type(s) with `lighting.mode` set and `dishes: null` (no inventory). An inventory snapshot always
 * carries a `dishes` array (`[...]` or `[]`) on at least one type. The destructive "empty absent
 * types" reconcile in `setDishTypes` must NOT run on a lighting echo — it would wipe every still-
 * stocked type the echo omits. True only for a non-empty frame whose every type has `dishes == null`.
 */
export const isLightingEchoFrame = (dishTypes?: Maybe<LocalDishType[]>): boolean =>
	Array.isArray(dishTypes) && dishTypes.length > 0 && dishTypes.every((dishType) => dishType?.dishes == null);

export const mergeDishTypeDishes = (
	originalDishes: Maybe<Dish[]> | undefined | null,
	updatedDishes: Maybe<DishSubscription[]> | undefined | null,
): Dish[] | null => {
	if (updatedDishes && updatedDishes.length === 0) {
		return [];
	}
	if (!updatedDishes && originalDishes) {
		return originalDishes;
	}

	if (!(originalDishes || updatedDishes)) {
		return null;
	}

	if (!originalDishes && updatedDishes) {
		return updatedDishes as Dish[];
	}

	const result = updatedDishes?.map((updatedDish) => {
		const originalDish = originalDishes?.find((dish) => dish.id === updatedDish.id);
		if (originalDish !== undefined && updatedDish) {
			return mergeValues(originalDish as Dish, updatedDish) as Dish;
		}
		return updatedDish as Dish;
	});

	if (result) {
		return result;
	}

	return null;
};

/**
 * The merged value of one field of a dish type. Over a stored value that is set, a null keeps it, and an object, an
 * array included, merges into it field by field. Otherwise a null or an undefined keeps the stored value unless that
 * is null, and any other value replaces it.
 */
const mergeDishTypeField = (originalValue: unknown, updatedValue: unknown) => {
	if (originalValue && typeof updatedValue === "object") {
		if (updatedValue === null) {
			return originalValue;
		}
		return mergeDishType(originalValue as Partial<DishType>, updatedValue as Partial<DishTypeSubscription>);
	}
	if ((updatedValue === null && originalValue !== null) || (updatedValue === undefined && originalValue !== null)) {
		return originalValue;
	}
	return updatedValue;
};

// What the walk over an update's keys gives mergeDishType: the merged value of each field but the allergens, and the
// dish list and allergens that replace the update's own when they are set.
type MergedDishTypeKeys = { result: object; dishes: Dish[] | null; allergens: AllergenType[] | null };

/**
 * Walks the update's keys for mergeDishType. A missing dish list keeps the stored one, and an array merges with it
 * (mergeDishTypeDishes); allergens that are set replace the stored ones, and missing ones keep them. Every other key
 * goes through mergeDishTypeField, and so does a dish list that is not an array (mergeDishType drops that value).
 * Null when the dish list does not merge.
 */
const mergeDishTypeKeys = (
	originalDishType: Partial<DishType>,
	updatedDishType: Partial<DishTypeSubscription>,
): MergedDishTypeKeys | null => {
	let result = {};
	let dishes: Dish[] | null = null;
	let allergens: AllergenType[] | null = null;

	for (const key of Object.keys(updatedDishType ?? {})) {
		const originalValue = originalDishType[key as keyof DishType];
		const updatedValue = updatedDishType[key as keyof DishTypeSubscription];

		if (key === "dishes" && Array.isArray(originalDishType[key]) && !updatedValue && originalValue) {
			dishes = originalValue as Dish[];
		}

		if (key === "dishes" && Array.isArray(updatedDishType[key])) {
			const mergedDishes = mergeDishTypeDishes(originalDishType.dishes, updatedDishType.dishes);
			if (!mergedDishes) {
				return null;
			}
			dishes = mergedDishes;
		} else if (key === "allergens") {
			allergens = (updatedValue || originalValue) as AllergenType[];
		} else {
			result = {
				...result,
				[key]: mergeDishTypeField(originalValue, updatedValue),
			};
		}
	}
	return { result: result, dishes: dishes, allergens: allergens };
};

/**
 * Merges original dish type with updated dish type
 * @param originalDishType
 * @param updatedDishType
 */
export const mergeDishType = (originalDishType: Partial<DishType>, updatedDishType: Partial<DishTypeSubscription>) => {
	// When there is no original dish type, we want to return updated dish type
	if (!originalDishType && updatedDishType) {
		return updatedDishType as LocalDishType;
	}

	// When there is no updated dish type, we want to return original dish type
	if (!(updatedDishType || originalDishType)) {
		return null;
	}

	// If updated dish type updates some values in original one,we merge it
	const merged = mergeDishTypeKeys(originalDishType, updatedDishType);
	if (!merged) {
		return null;
	}
	return {
		...originalDishType,
		...updatedDishType,
		...omit(merged.result, "dishes", "allergens"),
		...(merged.dishes && { dishes: merged.dishes }),
		...(merged.allergens && { allergens: merged.allergens }),
	};
};

/**
 * Merges original dish types with updated dish types
 * @param originalDishTypes
 * @param updatedDishTypes
 */
export const mergeDishTypes = (
	originalDishTypes: LocalDishType[],
	updatedDishTypes: (LocalDishType | undefined)[],
): LocalDishType[] => {
	const updatedDishTypesSafe = updatedDishTypes.filter((dishType) => dishType !== undefined);

	if (updatedDishTypesSafe.length === 0 && originalDishTypes) {
		return originalDishTypes;
	}
	if ((!originalDishTypes || originalDishTypes.length === 0) && updatedDishTypesSafe.length > 0) {
		return updatedDishTypesSafe;
	}
	if (updatedDishTypesSafe.length === 0 && (!originalDishTypes || originalDishTypes.length === 0)) {
		return [];
	}

	// Create a map of updated dish types for quick lookup
	const updatedDishTypesMap = new Map(updatedDishTypesSafe.map((dt) => [dt.id, dt]));

	// Start with all original dish types, updated where applicable
	const result = originalDishTypes.map((originalDishType) => {
		const updatedDishType = updatedDishTypesMap.get(originalDishType.id);
		if (updatedDishType) {
			return mergeDishType(originalDishType, updatedDishType);
		}
		return originalDishType;
	});

	// Add any new dish types that weren't in the original array
	const originalDishTypeIds = new Set(originalDishTypes.map((dt) => dt.id));
	const newDishTypes = updatedDishTypesSafe.filter((updatedDishType) => !originalDishTypeIds.has(updatedDishType.id));

	return [...result, ...newDishTypes].filter(
		(dishType) => dishType !== null && dishType !== undefined,
	) as LocalDishType[];
};

// A shallow copy of an object or an array, and any other value as it is: what mergeValues writes the update into.
const shallowCopy = <T>(value: T): T => {
	if (typeof value === "object" && !Array.isArray(value)) {
		return { ...(value as object) } as T;
	}
	if (typeof value === "object" && Array.isArray(value)) {
		return [...value] as T;
	}
	return value;
};

/**
 * Merges the update's value under one key into `merged`, and tells whether mergeValues goes on to the update's next
 * key: a new order and the removed dishes are taken as they are and end the walk, and so does an undefined over a
 * stored value that is not null, which writes nothing.
 */
const mergeKey = <T>(merged: T, original: T, update: T, key: Extract<keyof T, string>): boolean => {
	/* If we receive new order, we want to replace the old one (eg. orderSummary modal
	is displayed with current order and we receive new order from subscription
	 */
	if (key === "order") {
		const originalOrder = original[key as keyof T] as Transaction;
		const updatedOrder = update[key as keyof T] as Transaction;
		if (originalOrder?.id !== updatedOrder?.id) {
			merged[key] = update[key];
			return false;
		}
	}

	// For removed dishes in order, we want to always use the updated value
	if (key === "removed") {
		merged[key] = update[key];
		return false;
	}

	// If we update object, we have to merge it recursively
	if (typeof update[key] === "object" && !Array.isArray(update[key])) {
		merged[key] = mergeValues(original[key], update[key]);
	} else if (Array.isArray(update[key])) {
		merged[key] = mergeValues(original[key], update[key]);
	} else if ((update[key] === null || update[key] === undefined) && original[key] !== null) {
		return false;
	} else {
		merged[key] = update[key];
	}
	return true;
};

/**
 * Merges two objects recursively based on our custom logic
 * @param original
 * @param update
 */
export const mergeValues = <T>(original: T, update: T): T => {
	// If both values are null, return null
	if (!(original || update)) {
		return original;
	}

	if (original !== null && update === null) {
		return original;
	}

	const merged = shallowCopy(original);
	if (!original && update) {
		return update;
	}

	if (typeof update === "object" && update !== null) {
		for (const key of Object.keys(update) as Extract<keyof T, string>[]) {
			if (!mergeKey(merged, original, update, key)) {
				break;
			}
		}
	}
	return merged;
};

export const deepOmit = <T, K>(obj: T, keysToOmit: string[]): K => {
	const keysToOmitIndex = keyBy(Array.isArray(keysToOmit) ? keysToOmit : [keysToOmit]);

	// create an index object of the keys that should be omitted

	function omitFromObject(source: T): K {
		// the inner function which will be called recursively

		// @ts-expect-error — lodash transform returns object, not K
		return transform(
			// @ts-expect-error — T is not assignable to lodash's overloaded transform parameter
			source,
			(result: object, value: object | string | number, key: string) => {
				// transform to a new object
				if (key in keysToOmitIndex) {
					// if the key is in the index skip it
					return;
				}

				// @ts-expect-error - dynamic key assignment
				result[key] = isObject(value) ? omitFromObject(value) : value;
				// if the key is an object run it through the inner function - omitFromObject
			},
		);
	}

	return omitFromObject(obj); // return the inner function result
};

const DISH_TYPE_HEADER_MAX_LENGTH = 28;

/**
 * How many words, from the first, the header of a split dish name takes: each word followed by a space, while the
 * header stays within DISH_TYPE_HEADER_MAX_LENGTH. The first word that would make it longer, or the word "s" or "z",
 * goes to the subheader, and every word after it goes there too.
 */
const countHeaderWords = (words: string[]) => {
	let header = "";
	for (const [index, word] of words.entries()) {
		const updatedHeader = `${header + word} `;
		if (updatedHeader.length > DISH_TYPE_HEADER_MAX_LENGTH || word === "s" || word === "z") {
			return index;
		}
		header = updatedHeader;
	}
	return words.length;
};

// Each word followed by a space, the way the header and the subheader of a split dish name end.
const joinWords = (words: string[]) => words.map((word) => `${word} `).join("");

/**
 * Splits dish name - this is utility function, that should improve readability of dish names,
 * it should keep the header in proper length and move the rest to subheader.
 * @param dishTypeName
 */
export const createSplitDishName = (dishTypeName: Maybe<string> | undefined) => {
	if (!dishTypeName) {
		return null;
	}

	const cleanedDishTypeName = dishTypeName.replace("X - ", "");

	if (cleanedDishTypeName.includes("|||")) {
		return {
			header: cleanedDishTypeName.split("|||")[0],
			subHeader: cleanedDishTypeName.split("|||")[1],
		};
	}

	const words = cleanedDishTypeName.split(" ");
	const headerWordCount = countHeaderWords(words);
	return {
		header: joinWords(words.slice(0, headerWordCount)),
		subHeader: joinWords(words.slice(headerWordCount)),
	};
};

/**
 * Temporary function to get dish type text value correctly formatted to title
 * and subtitle, this should be handled in administration and DB
 * @param dishType
 * @param language
 * @param value
 */
export const getDishTypeTextValue = (
	dishType: LocalDishType,
	language: "primary" | "secondary",
	value: "description" | "composition",
) => {
	if (dishType.description?.[language]?.[value] !== null) {
		return dishType.description?.[language]?.[value] as string;
	}
	return "No value";
};
export const parseInstructions = (dishTypeDescription: string | null) => {
	if (!dishTypeDescription) {
		return ["", ""];
	}
	if (dishTypeDescription.includes("|||")) {
		return dishTypeDescription.split("|||");
	}
	if (dishTypeDescription.includes("Použití")) {
		return dishTypeDescription.split("Použití");
	}
	return dishTypeDescription;
};

export function getShortenedFridgeId(fridgeId?: string | null) {
	if (!fridgeId) {
		return "";
	}
	return fridgeId.slice(-5);
}
